import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { StoreProvider } from "./context/StoreContext";
import { CartProvider } from "./context/CartContext";
import { BrandProvider, useBrand } from "./context/BrandContext";
import { ThemeProvider } from "./context/ThemeContext";

// Pages
import LandingPage from "./pages/LandingPage";
import StorePage from "./pages/StorePage";
import BookingPage from "./pages/BookingPage";
import ShopPage from "./pages/ShopPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import ReviewsPage from "./pages/ReviewsPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import UserDashboard from "./pages/UserDashboard";
import BarberDashboard from "./pages/BarberDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import PlatformAdminStores from "./pages/PlatformAdminStores";

// Components
import PlatformAdminBackButton from "./components/PlatformAdminBackButton";
import AuthCallback from "./components/AuthCallback";

// Protected Route Component
function ProtectedRoute({ children, roles }) {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Allow ADMIN_PLATFORM to access any route
  if (user?.role === 'ADMIN_PLATFORM') {
    return children;
  }
  
  if (roles && !roles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Role-based redirect
function RoleRedirect() {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/" replace />;
  
  switch (user.role) {
    case 'ADMIN_PLATFORM':
      return <Navigate to="/admin/platform" replace />;
    case 'ADMIN_STORE':
      return <Navigate to="/admin/store" replace />;
    case 'BARBER':
      return <Navigate to="/dashboard/barber" replace />;
    default:
      return <Navigate to="/dashboard" replace />;
  }
}

// Single Store Home - Redirects to the store page or admin dashboard
function SingleStoreHome() {
  const { storeSlug, loading } = useBrand();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const redirectedRef = React.useRef(false);
  
  // Redirect admins to their dashboard
  useEffect(() => {
    if (redirectedRef.current) return;
    if (isAuthenticated && user) {
      if (user.role === 'ADMIN_PLATFORM') {
        redirectedRef.current = true;
        navigate('/admin/platform', { replace: true });
        return;
      }
      if (user.role === 'ADMIN_STORE') {
        redirectedRef.current = true;
        navigate('/admin/store', { replace: true });
        return;
      }
      if (user.role === 'BARBER') {
        redirectedRef.current = true;
        navigate('/dashboard/barber', { replace: true });
        return;
      }
    }
  }, [isAuthenticated, user, navigate]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  // Platform Admin NON deve essere reindirizzato allo store
  if (isAuthenticated && user?.role === 'ADMIN_PLATFORM') {
    return null;
  }
  
  if (storeSlug) {
    return <Navigate to={`/s/${storeSlug}`} replace />;
  }
  
  return <LandingPage />;
}

// Routes for Single-Store Mode (white-label)
function SingleStoreRoutes() {
  const { storeSlug } = useBrand();
  
  return (
    <Routes>
      {/* Home redirects to store page */}
      <Route path="/" element={<SingleStoreHome />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ForgotPasswordPage />} />
      
      {/* Store Routes - Also accessible without /s/:slug prefix */}
      <Route path="/prenota" element={<BookingPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/carrello" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/recensioni" element={<ReviewsPage />} />
      
      {/* Store Routes with slug (for compatibility) */}
      <Route path="/s/:slug" element={<StorePage />} />
      <Route path="/s/:slug/prenota" element={<BookingPage />} />
      <Route path="/s/:slug/shop" element={<ShopPage />} />
      <Route path="/s/:slug/carrello" element={<CartPage />} />
      <Route path="/s/:slug/checkout" element={<CheckoutPage />} />
      <Route path="/s/:slug/recensioni" element={<ReviewsPage />} />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* User Dashboard */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute roles={['USER']}>
            <UserDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Barber Dashboard */}
      <Route 
        path="/dashboard/barber" 
        element={
          <ProtectedRoute roles={['BARBER']}>
            <BarberDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Store Admin Dashboard - anche Platform Admin può accedere (impersonificazione) */}
      <Route 
        path="/admin/store/*" 
        element={
          <ProtectedRoute roles={['ADMIN_STORE', 'ADMIN_PLATFORM']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Platform Admin - Store Selector (anche in single-store mode) */}
      <Route 
        path="/admin/platform/*" 
        element={
          <ProtectedRoute roles={['ADMIN_PLATFORM']}>
            <PlatformAdminStores />
          </ProtectedRoute>
        } 
      />
      
      {/* Role-based redirect after login */}
      <Route path="/redirect" element={<RoleRedirect />} />
      
      {/* 404 - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Routes for Multi-Store Mode (AppLook platform)
function MultiStoreRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ForgotPasswordPage />} />
      
      {/* Store Routes */}
      <Route path="/s/:slug" element={<StorePage />} />
      <Route path="/s/:slug/prenota" element={<BookingPage />} />
      <Route path="/s/:slug/shop" element={<ShopPage />} />
      <Route path="/s/:slug/carrello" element={<CartPage />} />
      <Route path="/s/:slug/checkout" element={<CheckoutPage />} />
      <Route path="/s/:slug/recensioni" element={<ReviewsPage />} />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* User Dashboard */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute roles={['USER']}>
            <UserDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Barber Dashboard */}
      <Route 
        path="/dashboard/barber" 
        element={
          <ProtectedRoute roles={['BARBER']}>
            <BarberDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Store Admin Dashboard - anche Platform Admin può accedere (impersonificazione) */}
      <Route 
        path="/admin/store/*" 
        element={
          <ProtectedRoute roles={['ADMIN_STORE', 'ADMIN_PLATFORM']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Platform Admin - Store Selector */}
      <Route 
        path="/admin/platform/*" 
        element={
          <ProtectedRoute roles={['ADMIN_PLATFORM']}>
            <PlatformAdminStores />
          </ProtectedRoute>
        } 
      />
      
      {/* Role-based redirect after login */}
      <Route path="/redirect" element={<RoleRedirect />} />
      
      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// OAuth Callback Handler - Check URL hash BEFORE rendering routes
function AppRouter() {
  const location = useLocation();
  
  // Check URL hash for session_id (from Google OAuth callback)
  // This must happen synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return <AppRoutesInner />;
}

// Inner routes component
function AppRoutesInner() {
  const { isSingleStore, loading } = useBrand();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  return isSingleStore ? <SingleStoreRoutes /> : <MultiStoreRoutes />;
}

// Main App Routes - Chooses based on mode (legacy, kept for compatibility)
function AppRoutes() {
  return <AppRouter />;
}

function App() {
  return (
    <ThemeProvider>
      <BrandProvider>
        <AuthProvider>
          <StoreProvider>
            <CartProvider>
              <BrowserRouter>
                <AppRoutes />
                <PlatformAdminBackButton />
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            </CartProvider>
          </StoreProvider>
        </AuthProvider>
      </BrandProvider>
    </ThemeProvider>
  );
}

export default App;
